import React, { useMemo, useRef } from "react";
import {
  useGame,
  usePlayer,
  usePlayers,
} from "@empirica/core/player/classic/react";
import { Button } from "../components/Button";
import { DonutChart } from "../negotiation/NegotiationDonutCharts";
import { getCounterpartCopy, isLossFrameDesign } from "../payoffFrame";
import { showsPerformanceFeedback } from "../../../shared/experimentBehavior.js";
import { Stage2ContributionQuestion } from "./Stage2ContributionQuestion";
import {
  CONTRIBUTION_AFTER_RESULTS,
  shouldAskContribution,
} from "../../../shared/contributionAssessment.js";

const GAME2_SEGMENT_BORDER_COLOR = "rgba(17, 24, 39, 0.8)";
const GAME1_SEGMENT_BORDER_COLOR = "rgba(107, 114, 128, 0.62)";
const BAR_SEGMENT_OUTLINE = `inset 0 0 0 7px ${GAME2_SEGMENT_BORDER_COLOR}`;
const GAME1_BAR_SEGMENT_OUTLINE = `inset 0 0 0 7px ${GAME1_SEGMENT_BORDER_COLOR}`;
const LEGEND_KEY_OUTLINE = `inset 0 0 0 3px ${GAME2_SEGMENT_BORDER_COLOR}`;
const GAME1_LEGEND_KEY_OUTLINE = `inset 0 0 0 3px ${GAME1_SEGMENT_BORDER_COLOR}`;
const GAME1_SEGMENT_OUTLINE_PROPS = {
  barOutline: GAME1_BAR_SEGMENT_OUTLINE,
  legendOutline: GAME1_LEGEND_KEY_OUTLINE,
};
const OPPONENT_LOSS_BACKGROUND =
  "repeating-linear-gradient(135deg, #9ca3af 0 10px, #dc2626 10px 20px)";
const OPPONENT_SAVE_BACKGROUND =
  "repeating-linear-gradient(135deg, #9ca3af 0 10px, #059669 10px 20px)";
const CONTEXT_LOST_COLOR = "#fca5a5";
const CONTEXT_SAVED_COLOR = "#86efac";
const CONTEXT_TAKEN_BACKGROUND =
  "repeating-linear-gradient(135deg, #fca5a5 0 10px, #86efac 10px 20px)";
const CONTEXT_SAVED_FROM_OPPONENT_BACKGROUND =
  "repeating-linear-gradient(135deg, #86efac 0 10px, #fca5a5 10px 20px)";
const CONTEXT_SAVINGS_AT_STAKE_BACKGROUND =
  "repeating-linear-gradient(135deg, #dc2626 0 10px, #fbbf24 10px 20px)";
const CONTEXT_SAVINGS_OFFER_BACKGROUND =
  "repeating-linear-gradient(135deg, #16a34a 0 10px, #fbbf24 10px 20px)";

function dollars(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function seconds(value) {
  return `${Number(value || 0).toFixed(1)} seconds`;
}

function wholeSeconds(value) {
  return `${Math.round(Number(value || 0))} second`;
}

function clampMoney(value, maxValue = Infinity) {
  const number = Number(value);
  const maxNumber = Number(maxValue);
  const safeValue = Number.isFinite(number) ? number : 0;
  const safeMax = Number.isFinite(maxNumber) ? Math.max(0, maxNumber) : Infinity;

  return Math.max(0, Math.min(safeValue, safeMax));
}

function segmentFillStyle(segment) {
  if (segment.background) {
    return { background: segment.background };
  }

  return { backgroundColor: segment.color };
}

function segmentLabel(segment) {
  if (segment.labelOrigin || segment.labelEmphasis) {
    return (
      <>
        {segment.labelOrigin ? <span>{segment.labelOrigin} </span> : null}
        <strong
          className={`font-semibold ${
            segment.labelEmphasisClassName || "text-gray-900"
          }`}
        >
          {segment.labelEmphasis || segment.label}
        </strong>
      </>
    );
  }

  return segment.label;
}

function MoneySegmentBar({
  segments,
  total = null,
  legendTextClassName = "text-xl",
  legendSwatchClassName = "h-4 w-4",
  barHeightClassName = "h-10",
  showLegendPercent = false,
}) {
  const barRef = useRef(null);
  const dragInteractionRef = useRef(null);
  const normalizedSegments = segments
    .map((segment) => ({
      ...segment,
      value: Math.max(0, Number(segment.value) || 0),
    }));
  const safeSegments = normalizedSegments.filter((segment) => segment.value > 0);
  const legendSegments = normalizedSegments.filter(
    (segment) => segment.value > 0 || segment.showZeroValueLegend
  );
  const safeTotal = Math.max(
    0,
    Number(total) || safeSegments.reduce((sum, segment) => sum + segment.value, 0)
  );
  let cumulativeSegmentValue = 0;
  const segmentRanges = safeSegments.map((segment) => {
    const startValue = cumulativeSegmentValue;
    cumulativeSegmentValue += segment.value;

    return {
      segment,
      startValue,
      endValue: cumulativeSegmentValue,
    };
  });

  function interactionKey(segment) {
    return segment.interactionGroup || segment.label;
  }

  function handleSegmentSelect(event, selectedInteractionKey) {
    const bar = barRef.current;
    if (!bar || safeTotal <= 0) {
      return;
    }

    const rect = bar.getBoundingClientRect();
    const pointerRatio = rect.width > 0
      ? Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
      : 0;
    const pointerValue = pointerRatio * safeTotal;
    const selectableRanges = segmentRanges.filter(
      ({ segment }) =>
        typeof segment.onSelect === "function" &&
        interactionKey(segment) === selectedInteractionKey
    );

    if (selectableRanges.length === 0) {
      return;
    }

    const firstRange = selectableRanges[0];
    const lastRange = selectableRanges[selectableRanges.length - 1];
    const selectedRange =
      selectableRanges.find(
        ({ startValue, endValue }) =>
          pointerValue >= startValue && pointerValue <= endValue
      ) || (pointerValue < firstRange.startValue ? firstRange : lastRange);
    const boundedValue = Math.max(
      selectedRange.startValue,
      Math.min(pointerValue, selectedRange.endValue)
    );
    const segmentRatio = selectedRange.segment.value > 0
      ? (boundedValue - selectedRange.startValue) / selectedRange.segment.value
      : 0;

    selectedRange.segment.onSelect({
      event,
      ratio: segmentRatio,
      segment: selectedRange.segment,
      segmentStartValue: selectedRange.startValue,
      segmentEndValue: selectedRange.endValue,
      value: boundedValue,
    });
  }

  function handlePointerDown(event, segment) {
    if (typeof segment.onSelect !== "function") {
      return;
    }

    event.preventDefault();
    const selectedInteractionKey = interactionKey(segment);
    dragInteractionRef.current = {
      pointerId: event.pointerId,
      interactionKey: selectedInteractionKey,
    };
    barRef.current?.setPointerCapture?.(event.pointerId);
    handleSegmentSelect(event, selectedInteractionKey);
  }

  function handlePointerMove(event) {
    const activeInteraction = dragInteractionRef.current;
    if (!activeInteraction || activeInteraction.pointerId !== event.pointerId) {
      return;
    }

    event.preventDefault();
    handleSegmentSelect(event, activeInteraction.interactionKey);
  }

  function handlePointerEnd(event) {
    const activeInteraction = dragInteractionRef.current;
    if (!activeInteraction || activeInteraction.pointerId !== event.pointerId) {
      return;
    }

    handleSegmentSelect(event, activeInteraction.interactionKey);
    dragInteractionRef.current = null;
    barRef.current?.releasePointerCapture?.(event.pointerId);
  }

  return (
    <div className="space-y-3">
      <div
        ref={barRef}
        className={`flex ${barHeightClassName} overflow-hidden rounded-md bg-gray-100`}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
      >
        {safeTotal > 0 && safeSegments.length > 0 ? (
          safeSegments.map((segment) => {
            const interactive = typeof segment.onSelect === "function";

            return (
              <div
                key={segment.label}
                className={`h-full ${interactive ? "cursor-ew-resize select-none" : ""}`}
                style={{
                  width: `${Math.max(0, Math.min(100, (segment.value / safeTotal) * 100))}%`,
                  ...segmentFillStyle(segment),
                  boxShadow: segment.barOutline || BAR_SEGMENT_OUTLINE,
                  touchAction: interactive ? "none" : undefined,
                }}
                title={segment.title || `${segment.label}: ${dollars(segment.value)}`}
                onPointerDown={
                  interactive ? (event) => handlePointerDown(event, segment) : undefined
                }
              />
            );
          })
        ) : (
          <div className="h-full w-full bg-gray-100" />
        )}
      </div>

      <div className="grid gap-2">
        {legendSegments.map((segment) => {
          const segmentValue = Math.max(0, Number(segment.value) || 0);
          const percent = safeTotal > 0
            ? segmentValue / safeTotal * 100
            : 0;
          const showNegativeValue = segment.negativeLegendValue && segmentValue > 0;
          const valueClassName = showNegativeValue
            ? "text-red-700"
            : segment.legendValueClassName || "text-gray-900";
          const valuePrefix = showNegativeValue ? "-" : "";

          return (
            <div
              key={segment.label}
              className={`grid grid-cols-[1.25rem_minmax(0,1fr)_auto] items-center gap-3 ${legendTextClassName}`}
            >
              <span
                className={`${legendSwatchClassName} rounded-sm`}
                style={{
                  ...segmentFillStyle(segment),
                  boxShadow: segment.legendOutline || LEGEND_KEY_OUTLINE,
                }}
              />
              <span className="text-gray-700">{segmentLabel(segment)}</span>
              <span className={`grid grid-cols-[7.5rem_6.5rem] justify-end text-right ${valueClassName}`}>
                <span className="font-semibold tabular-nums">
                  {valuePrefix}{dollars(segment.value)}
                </span>
                {showLegendPercent ? (
                  <span className="font-normal tabular-nums">
                    ({percent.toFixed(1)}%)
                  </span>
                ) : null}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LossSavedCard({
  title,
  savedDollars,
  lostDollars,
  inCircleSeconds,
  durationSeconds,
}) {
  const totalAtStake = savedDollars + lostDollars;

  return (
    <div className="rounded-md border border-gray-200 bg-white p-5 space-y-4">
      <div>
        <div className="text-3xl font-semibold text-gray-900">{title}</div>
        <div className="mt-1 text-xl text-gray-600">
          Cursor in circle for <strong>{Number(inCircleSeconds || 0).toFixed(1)}</strong>
          /{Number(durationSeconds || 0).toFixed(0)} seconds
        </div>
      </div>

      <MoneySegmentBar
        total={totalAtStake}
        legendTextClassName="text-2xl"
        legendSwatchClassName="h-5 w-5"
        showLegendPercent
        segments={[
          {
            label: "Lost",
            value: lostDollars,
            color: "#dc2626",
            negativeLegendValue: true,
          },
          {
            label: "Saved",
            value: savedDollars,
            color: "#059669",
            legendValueClassName: "text-emerald-700",
          },
        ]}
      />
    </div>
  );
}

function ContributionCard({
  title,
  titleSuffix,
  dollarsValue,
  secondsValue,
  totalSecondsValue,
  percentValue,
  colorClassName,
  barClassName,
}) {
  const safePercent = Math.max(0, Math.min(100, Number(percentValue) || 0));

  return (
    <div className="rounded-md border border-gray-200 bg-white p-4 space-y-3">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-4 gap-y-1">
        <div className="col-span-2 whitespace-nowrap text-2xl text-gray-900">
          <span className="font-semibold">{title}</span>
          {titleSuffix ? ` ${titleSuffix}` : null}
        </div>
        <div className={`text-3xl font-semibold ${colorClassName}`}>
          {dollars(dollarsValue)}
        </div>
        <div className="whitespace-nowrap text-right text-2xl text-gray-900">
          <span className={`font-semibold ${colorClassName}`}>
            {Number(secondsValue || 0).toFixed(1)}
          </span>
          /{Number(totalSecondsValue || 0).toFixed(0)} seconds
        </div>
      </div>
      <div className="h-4 overflow-hidden rounded bg-gray-100">
        <div
          className={`h-full rounded ${barClassName}`}
          style={{ width: `${safePercent}%` }}
        />
      </div>
    </div>
  );
}

function SectionHeader({ title, children }) {
  return (
    <div>
      <h2 className="text-4xl font-semibold text-gray-900">{title}</h2>
      {children ? <p className="mt-1 text-2xl text-gray-600">{children}</p> : null}
    </div>
  );
}

export function Stage2PayAtStakeVisualization({
  stage1 = {},
  stage2 = {},
  design = {},
  player,
  players = [],
  game2SavingsAtStakeSegments = null,
  showSectionHeader = true,
  showStartingAllocationInRemaining = false,
  game2MoneyKeptDollars = null,
}) {
  if (!player || !stage2) {
    return null;
  }

  const counterpart = getCounterpartCopy(design);
  const partner = players.find((p) => p.id !== player.id);
  const teamWinningsBeforeAdjustment = Number(stage2.teamWinningsBeforeAdjustment ?? 0);
  const teamInCircleSeconds = Number(stage2.teamInCircleSeconds ?? 0);
  const gameDurationSeconds = Number(stage2.durationSeconds ?? teamInCircleSeconds);
  const trackingDollarsPerSecond = Math.max(
    0,
    Number(stage2.trackingDollarsPerSecond ?? design.trackingDollarsPerSecond ?? 0)
  );
  const configuredStage2StakeDollars = Number(
    stage2.stage2MaxPerParticipant ?? design.stage2MaxPerParticipant
  );
  const stage2StakeDollars = Math.max(
    0,
    Number.isFinite(configuredStage2StakeDollars)
      ? configuredStage2StakeDollars
      : gameDurationSeconds * trackingDollarsPerSecond
  );
  const teamPlayerCount = Math.max(
    1,
    players.length || Number(design.playerCount) || 2
  );
  const stage2TeamStakeDollars = stage2StakeDollars * teamPlayerCount;
  const teamSavedDollars = clampMoney(
    teamWinningsBeforeAdjustment,
    stage2TeamStakeDollars
  );
  const stage1ScoresByPlayerId = Object.fromEntries(
    (stage1.scores || []).map((score) => [
      score.playerId,
      Number(score.inCircleSeconds ?? 0),
    ])
  );
  const stage1DurationSeconds = Number(stage1.durationSeconds ?? 0);
  const stage1TrackingDollarsPerSecond = Math.max(
    0,
    Number(stage1.trackingDollarsPerSecond ?? design.trackingDollarsPerSecond ?? 0)
  );
  const stage1StakeDollars = Math.max(
    0,
    Number(
      stage1.stage1MaxPerParticipant ??
        design.stage1MaxPerParticipant ??
        stage1DurationSeconds * stage1TrackingDollarsPerSecond
    ) || 0
  );
  const stage1YourSavedDollars = clampMoney(
    (stage1ScoresByPlayerId[player.id] ?? 0) * stage1TrackingDollarsPerSecond,
    stage1StakeDollars
  );
  const stage1PartnerSavedDollars = clampMoney(
    (partner ? stage1ScoresByPlayerId[partner.id] ?? 0 : 0) *
      stage1TrackingDollarsPerSecond,
    stage1StakeDollars
  );
  const stage1YourLostDollars = clampMoney(
    stage1StakeDollars - stage1YourSavedDollars,
    stage1StakeDollars
  );
  const stage1SavedFromOpponentDollars =
    stage1.winnerId === player.id ? stage1PartnerSavedDollars : 0;
  const stage1SavedFromOpponentOffsetDollars = Math.min(
    stage1SavedFromOpponentDollars,
    stage1YourLostDollars
  );
  const stage1SavedFromOpponentSurplusDollars = Math.max(
    0,
    stage1SavedFromOpponentDollars - stage1YourLostDollars
  );
  const stage1LostAfterOpponentSavedDollars = Math.max(
    0,
    stage1YourLostDollars - stage1SavedFromOpponentOffsetDollars
  );
  const stage1SavedFromOpponentSegment =
    stage1SavedFromOpponentDollars > 0
      ? [{
        label: `Game 1 Saved From ${counterpart.capitalizedTerm}`,
        labelOrigin: "Game 1",
        labelEmphasis: `Saved From ${counterpart.capitalizedTerm}`,
        value: stage1SavedFromOpponentDollars,
        background: CONTEXT_SAVED_FROM_OPPONENT_BACKGROUND,
        legendValueClassName: "text-emerald-700",
        ...GAME1_SEGMENT_OUTLINE_PROPS,
      }]
      : [];
  const stage1TakenFromYouDollars =
    stage1.loserId === player.id ? stage1YourSavedDollars : 0;
  const stage1TakenFromYouSegment =
    stage1TakenFromYouDollars > 0
      ? [{
        label: "Game 1 Lost (Taken From You)",
        labelOrigin: "Game 1",
        labelEmphasis: "Lost (Taken From You)",
        value: stage1TakenFromYouDollars,
        background: CONTEXT_TAKEN_BACKGROUND,
        negativeLegendValue: true,
        ...GAME1_SEGMENT_OUTLINE_PROPS,
      }]
      : [];
  const stage1SavedKeptDollars =
    stage1.winnerId === player.id ? stage1YourSavedDollars : 0;
  const stage1SavedKeptSegment =
    stage1SavedKeptDollars > 0
      ? [{
        label: "Game 1 Saved",
        labelOrigin: "Game 1",
        labelEmphasis: "Saved",
        value: stage1SavedKeptDollars,
        color: CONTEXT_SAVED_COLOR,
        legendValueClassName: "text-emerald-700",
        ...GAME1_SEGMENT_OUTLINE_PROPS,
      }]
      : [];
  const game2LossBasisDollars = stage2StakeDollars;
  const game2LostAfterSavingsDollars = Math.max(
    0,
    game2LossBasisDollars - teamSavedDollars
  );
  const game2SavingsEatingLossDollars = Math.min(
    teamSavedDollars,
    game2LossBasisDollars
  );
  const game2SurplusAtStakeDollars = Math.max(
    0,
    teamSavedDollars - game2LossBasisDollars
  );
  const game2SavingsAtStakeDollars =
    game2SavingsEatingLossDollars + game2SurplusAtStakeDollars;
  const game2LostSegment =
    game2LostAfterSavingsDollars > 0
      ? [{
        label: "Game 2 Lost",
        labelOrigin: "Game 2",
        labelEmphasis: "Lost",
        value: game2LostAfterSavingsDollars,
        color: "#dc2626",
        negativeLegendValue: true,
      }]
      : [];
  const game2SavingsAtStakeSegment =
    game2SavingsAtStakeDollars > 0
      ? [{
        label: "Game 2 Savings At Stake",
        labelOrigin: "Game 2",
        labelEmphasis: "Savings At Stake",
        value: game2SavingsAtStakeDollars,
        background: CONTEXT_SAVINGS_AT_STAKE_BACKGROUND,
        legendValueClassName: "text-amber-700",
      }]
      : [];
  const displayedGame2SavingsAtStakeSegments =
    Array.isArray(game2SavingsAtStakeSegments)
      ? game2SavingsAtStakeSegments.filter(
        (segment) =>
          Math.max(0, Number(segment.value) || 0) > 0 ||
          segment.showZeroValueLegend
      )
      : game2SavingsAtStakeSegment;
  const moneyContextTotalDollars =
    stage1StakeDollars +
    stage1SavedFromOpponentSurplusDollars +
    game2LostAfterSavingsDollars +
    game2SavingsAtStakeDollars;
  const playerStartingAllocationDollars =
    stage1StakeDollars + stage2StakeDollars;
  const customGame2MoneyKeptDollars = Number(game2MoneyKeptDollars);
  const game2HeadlineChangeDollars = Number.isFinite(customGame2MoneyKeptDollars)
    ? clampMoney(customGame2MoneyKeptDollars, Infinity) - stage2StakeDollars
    : game2SurplusAtStakeDollars - game2LostAfterSavingsDollars;
  const headlineChangeDollars =
    stage1SavedFromOpponentSurplusDollars -
    stage1LostAfterOpponentSavedDollars -
    stage1TakenFromYouDollars +
    game2HeadlineChangeDollars;
  const headlineIsPositive = headlineChangeDollars > 0.005;
  const headlineIsNegative = headlineChangeDollars < -0.005;
  const headlineClassName = headlineIsPositive
    ? "text-emerald-700"
    : headlineIsNegative
    ? "text-red-700"
    : "text-gray-900";
  const headlinePrefix = headlineIsPositive
    ? "+"
    : headlineIsNegative
    ? "-"
    : "";
  const headlineAmountDollars =
    headlineIsPositive || headlineIsNegative
      ? Math.abs(headlineChangeDollars)
      : 0;
  const payAtStakeRemainingDollars = Math.max(
    0,
    playerStartingAllocationDollars + headlineChangeDollars
  );

  return (
    <section className="rounded-md border border-gray-200 bg-white p-5 space-y-5">
      {showSectionHeader ? (
        <SectionHeader title="Pay at Stake">
          <span className="text-3xl">
            What happened to the{" "}
            <span className="font-semibold text-gray-900">
              {dollars(playerStartingAllocationDollars)}
            </span>{" "}
            you started with.
          </span>
        </SectionHeader>
      ) : null}

      <div className="rounded-md border border-gray-200 p-5 space-y-4">
        <div className="mt-1 flex flex-wrap items-baseline gap-x-6 gap-y-2">
          <div className={`text-5xl font-semibold ${headlineClassName}`}>
            {headlinePrefix}{dollars(headlineAmountDollars)}
          </div>
          <div className="text-3xl text-gray-700">
            You have{" "}
            <strong className="text-gray-900">
              {dollars(payAtStakeRemainingDollars)}
            </strong>{" "}
            remaining
            {showStartingAllocationInRemaining ? (
              <>
                {" "}from{" "}
                <strong className="text-gray-900">
                  {dollars(playerStartingAllocationDollars)}
                </strong>
              </>
            ) : null}
            .
          </div>
        </div>

        <div className="text-3xl text-gray-700">
          You and your {counterpart.term} saved{" "}
          <strong className="text-amber-700">
            {dollars(teamSavedDollars)}
          </strong>{" "}
          in Game 2.
        </div>

        <div className="border-t border-gray-200 pt-5 space-y-4">
          <MoneySegmentBar
            total={moneyContextTotalDollars}
            legendTextClassName="text-3xl"
            legendSwatchClassName="h-6 w-6"
            barHeightClassName="h-16"
            showLegendPercent
            segments={[
              {
                label: "Game 1 Lost",
                labelOrigin: "Game 1",
                labelEmphasis: "Lost",
                value: stage1LostAfterOpponentSavedDollars,
                color: CONTEXT_LOST_COLOR,
                negativeLegendValue: true,
                ...GAME1_SEGMENT_OUTLINE_PROPS,
              },
              ...stage1TakenFromYouSegment,
              ...stage1SavedFromOpponentSegment,
              ...stage1SavedKeptSegment,
              ...game2LostSegment,
              ...displayedGame2SavingsAtStakeSegments,
            ]}
          />
        </div>
      </div>
    </section>
  );
}

export function LossFrameDemandHistoryVisualization({
  stage1 = {},
  stage2 = {},
  design = {},
  player,
  players = [],
  demand,
  demandLabel = "Your Demand",
  demandColor = "#4f46e5",
  demandLabelClassName = "text-gray-900",
  demandLegendValueClassName = "text-blue-700",
  savingsLeftOverLabel = "Savings Left Over",
  demandSelectionTitle = "Click or drag to set demanded savings",
  showZeroValueLegendItems = false,
  onDemandChange,
}) {
  const savingsAtStakeDollars = Math.max(
    0,
    Number(stage2.teamWinningsBeforeAdjustment ?? 0)
  );
  const demandWithinSavings = clampMoney(demand, savingsAtStakeDollars);
  const savingsLeftOver = Math.max(0, savingsAtStakeDollars - demandWithinSavings);
  const canSelectDemand = typeof onDemandChange === "function";
  const savingsInteractionGroup = "game2-savings-at-stake-demand";

  function handleSavingsClick(segmentStartDollars) {
    return ({ ratio, segment }) => {
      const clickedDollarsFromLeft =
        segmentStartDollars + (ratio * Math.max(0, Number(segment.value) || 0));
      const nextDemand = clampMoney(
        savingsAtStakeDollars - clickedDollarsFromLeft,
        savingsAtStakeDollars
      );

      onDemandChange(nextDemand);
    };
  }

  return (
    <Stage2PayAtStakeVisualization
      stage1={stage1}
      stage2={stage2}
      design={design}
      player={player}
      players={players}
      showSectionHeader={false}
      showStartingAllocationInRemaining
      game2MoneyKeptDollars={demandWithinSavings}
      game2SavingsAtStakeSegments={[
        {
          label: `Game 2 ${savingsLeftOverLabel}`,
          labelOrigin: "Game 2",
          labelEmphasis: savingsLeftOverLabel,
          value: savingsLeftOver,
          background: CONTEXT_SAVINGS_AT_STAKE_BACKGROUND,
          legendValueClassName: "text-amber-700",
          showZeroValueLegend: showZeroValueLegendItems,
          interactionGroup: savingsInteractionGroup,
          onSelect: canSelectDemand ? handleSavingsClick(0) : undefined,
          title: canSelectDemand ? demandSelectionTitle : undefined,
        },
        {
          label: `Game 2 ${demandLabel}`,
          labelOrigin: "Game 2",
          labelEmphasis: demandLabel,
          labelEmphasisClassName: demandLabelClassName,
          value: demandWithinSavings,
          color: demandColor,
          background:
            demandLabel === "Savings Offer"
              ? CONTEXT_SAVINGS_OFFER_BACKGROUND
              : undefined,
          legendValueClassName: demandLegendValueClassName,
          showZeroValueLegend: showZeroValueLegendItems,
          interactionGroup: savingsInteractionGroup,
          onSelect: canSelectDemand ? handleSavingsClick(savingsLeftOver) : undefined,
          title: canSelectDemand ? demandSelectionTitle : undefined,
        },
      ]}
    />
  );
}

export function LossFrameDemandBarCard({
  title,
  demand,
  savingsAtStakeDollars,
  actorLabel = "You",
  demandLabel = "Demanded Savings",
  demandColor = "#16a34a",
  demandLabelClassName = "text-gray-900",
  demandLegendValueClassName = "text-green-700",
  potentialLossesLabel = "Potential Losses",
  demandActionPast = "demanded",
  demandPercentLabel = "Demanded savings",
}) {
  const safeSavingsAtStakeDollars = Math.max(
    0,
    Number(savingsAtStakeDollars) || 0
  );
  const demandWithinSavings = clampMoney(demand, safeSavingsAtStakeDollars);
  const potentialLosses = Math.max(
    0,
    safeSavingsAtStakeDollars - demandWithinSavings
  );
  const demandPercent = safeSavingsAtStakeDollars > 0
    ? Math.round((demandWithinSavings / safeSavingsAtStakeDollars) * 100)
    : 0;

  return (
    <div className="rounded-md border border-gray-200 bg-white p-5 space-y-4">
      <div>
        <h3 className="text-3xl font-semibold text-gray-900">{title}</h3>
        <p className="mt-1 text-2xl text-gray-600">
          {actorLabel} {demandActionPast}{" "}
          <strong className={demandLegendValueClassName}>
            {dollars(demandWithinSavings)}
          </strong>{" "}
          of the{" "}
          <strong className="text-gray-900">
            {dollars(safeSavingsAtStakeDollars)}
          </strong>{" "}
          Game 2 savings.
        </p>
      </div>

      <MoneySegmentBar
        total={safeSavingsAtStakeDollars}
        legendTextClassName="text-2xl"
        legendSwatchClassName="h-5 w-5"
        barHeightClassName="h-14"
        showLegendPercent
        segments={[
          {
            label: potentialLossesLabel,
            labelEmphasis: potentialLossesLabel,
            value: potentialLosses,
            background: CONTEXT_SAVINGS_AT_STAKE_BACKGROUND,
            legendValueClassName: "text-amber-700",
            showZeroValueLegend: true,
          },
          {
            label: demandLabel,
            labelEmphasis: demandLabel,
            labelEmphasisClassName: demandLabelClassName,
            value: demandWithinSavings,
            color: demandColor,
            background:
              demandLabel === "Savings Offer"
                ? CONTEXT_SAVINGS_OFFER_BACKGROUND
                : undefined,
            legendValueClassName: demandLegendValueClassName,
          },
        ]}
      />

      <div className="text-xl text-gray-600">
        {demandPercentLabel}: <strong>{demandPercent}%</strong>
      </div>
    </div>
  );
}

export function LossFrameStage1MoneyKeptCard({
  title = "Game 1 Money Kept",
  stage1StakeDollars,
  yourSavedDollars,
  partnerSavedDollars,
  youWon,
  tied = false,
  design,
}) {
  const counterpart = getCounterpartCopy(design);
  const safeStake = Math.max(0, Number(stage1StakeDollars) || 0);
  const safeYourSaved = clampMoney(yourSavedDollars, safeStake);
  const safePartnerSaved = Math.max(0, Number(partnerSavedDollars) || 0);
  const yourLost = Math.max(0, safeStake - safeYourSaved);
  const savedFromOpponent = youWon ? Math.min(safePartnerSaved, yourLost) : 0;
  const surplusFromOpponent = youWon
    ? Math.max(0, safePartnerSaved - yourLost)
    : 0;
  const unreplacedLoss = youWon
    ? Math.max(0, yourLost - savedFromOpponent)
    : yourLost;
  const savedKept = youWon ? safeYourSaved : 0;
  const savedTakenByOpponent = youWon ? 0 : safeYourSaved;
  const game1MoneyKept = youWon
    ? safeYourSaved + savedFromOpponent + surplusFromOpponent
    : 0;
  const barTotal = safeStake + surplusFromOpponent;

  return (
    <div className="rounded-md border border-gray-200 bg-white p-5 space-y-4">
      <div>
        <h3 className="text-3xl font-semibold text-gray-900">{title}</h3>
        <p className="mt-1 text-2xl text-gray-600">
          You kept{" "}
          <strong className="text-green-700">
            {dollars(game1MoneyKept)}
          </strong>{" "}
          after starting Game 1 with{" "}
          <strong className="text-gray-900">{dollars(safeStake)}</strong>{" "}
          at stake.
        </p>
        {tied ? (
          <p className="mt-1 text-xl text-gray-600">
            Scores were tied, so the winner was selected by random draw.
          </p>
        ) : null}
      </div>

      <MoneySegmentBar
        total={barTotal}
        legendTextClassName="text-2xl"
        legendSwatchClassName="h-5 w-5"
        barHeightClassName="h-14"
        showLegendPercent
        segments={[
          {
            label: "Game 1 Lost",
            labelOrigin: "Game 1",
            labelEmphasis: "Lost",
            value: unreplacedLoss,
            color: CONTEXT_LOST_COLOR,
            negativeLegendValue: true,
          },
          {
            label: `Game 1 Saved Taken By ${counterpart.capitalizedTerm}`,
            labelOrigin: "Game 1",
            labelEmphasis: `Saved Taken By ${counterpart.capitalizedTerm}`,
            value: savedTakenByOpponent,
            background: CONTEXT_TAKEN_BACKGROUND,
            negativeLegendValue: true,
          },
          {
            label: `Game 1 Saved From ${counterpart.capitalizedTerm}`,
            labelOrigin: "Game 1",
            labelEmphasis: `Saved From ${counterpart.capitalizedTerm}`,
            value: savedFromOpponent,
            background: CONTEXT_SAVED_FROM_OPPONENT_BACKGROUND,
            legendValueClassName: "text-green-700",
          },
          {
            label: "Game 1 Money You Saved",
            labelOrigin: "Game 1",
            labelEmphasis: "Money You Saved",
            value: savedKept,
            color: CONTEXT_SAVED_COLOR,
            legendValueClassName: "text-green-700",
          },
          {
            label: `Game 1 Surplus From ${counterpart.capitalizedTerm}`,
            labelOrigin: "Game 1",
            labelEmphasis: `Surplus From ${counterpart.capitalizedTerm}`,
            value: surplusFromOpponent,
            background: CONTEXT_SAVED_FROM_OPPONENT_BACKGROUND,
            legendValueClassName: "text-green-700",
          },
        ]}
      />
    </div>
  );
}

export function LossFrameMoneyKeptBarCard({
  title,
  subtitle = null,
  stage1 = {},
  stage2 = {},
  design = {},
  player,
  players = [],
  yourMoneyKept,
  totalDollars,
  lostToShrinkDollars = 0,
  chartClassName = "",
}) {
  const savedAmountDollars = Math.max(
    0,
    Number(totalDollars ?? stage2.teamWinningsBeforeAdjustment) || 0
  );
  const safeYourMoneyKept = clampMoney(yourMoneyKept, savedAmountDollars);
  const safeLostToShrink = clampMoney(
    lostToShrinkDollars,
    Math.max(0, savedAmountDollars - safeYourMoneyKept)
  );
  const lostInNegotiation = Math.max(
    0,
    savedAmountDollars - safeYourMoneyKept - safeLostToShrink
  );
  const outcomeStage2 = {
    ...stage2,
    teamWinningsBeforeAdjustment: savedAmountDollars,
  };

  if (!player) {
    return null;
  }

  const game2OutcomeSegments = [
    {
      label: "Game 2 Lost to Shrink",
      labelOrigin: "Game 2",
      labelEmphasis: "Lost to Shrink",
      value: safeLostToShrink,
      color: "#6b7280",
      negativeLegendValue: true,
    },
    {
      label: "Game 2 Lost in Negotiation",
      labelOrigin: "Game 2",
      labelEmphasis: "Lost in Negotiation",
      value: lostInNegotiation,
      background: CONTEXT_SAVINGS_AT_STAKE_BACKGROUND,
      negativeLegendValue: true,
    },
    {
      label: "Game 2 Money Kept",
      labelOrigin: "Game 2",
      labelEmphasis: "Money Kept",
      value: safeYourMoneyKept,
      color: "#16a34a",
      legendValueClassName: "text-green-700",
      showZeroValueLegend: true,
    },
  ];

  return (
    <div className={`space-y-3 ${chartClassName}`}>
      <div>
        <h3 className="text-3xl font-semibold text-gray-900">{title}</h3>
        {subtitle ? (
          <p className="mt-1 text-2xl text-gray-600">{subtitle}</p>
        ) : null}
      </div>

      <Stage2PayAtStakeVisualization
        stage1={stage1}
        stage2={outcomeStage2}
        design={design}
        player={player}
        players={players}
        showSectionHeader={false}
        showStartingAllocationInRemaining
        game2MoneyKeptDollars={safeYourMoneyKept}
        game2SavingsAtStakeSegments={game2OutcomeSegments}
      />
    </div>
  );
}

export function Stage2ResultStage() {
  const game = useGame();
  const player = usePlayer();
  const players = usePlayers();
  const stage2 = game.get("stage2");
  const stage1 = game.get("stage1") || {};
  const design = game.get("design") || {};
  const lossFrame = isLossFrameDesign(design);
  const counterpart = getCounterpartCopy(design);

  const allSubmitted = useMemo(
    () => players.every((p) => p.stage.get("submit")),
    [players]
  );

  function handleContinue() {
    if (shouldAskContribution(player, CONTRIBUTION_AFTER_RESULTS)) {
      player.stage.set("stage2ContributionQuestionShown", true);
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      return;
    }
    player.stage.set("submit", true);
  }

  if (!stage2) {
    return (
      <div className="max-w-3xl mx-auto px-8 py-6 text-center text-xl text-gray-500">
        Computing Stage 2 tracking results…
      </div>
    );
  }

  if (player.stage.get("stage2ContributionQuestionShown")) {
    return (
      <Stage2ContributionQuestion
        timing={CONTRIBUTION_AFTER_RESULTS}
        next={() => player.stage.set("submit", true)}
        disabled={player.stage.get("submit") === true}
      />
    );
  }

  const teamInCircleSeconds = Number(stage2.teamInCircleSeconds ?? 0);
  const gameDurationSeconds = Number(stage2.durationSeconds ?? teamInCircleSeconds);
  const teamWinningsBeforeAdjustment = Number(stage2.teamWinningsBeforeAdjustment ?? 0);
  const partner = players.find((p) => p.id !== player.id);
  const showPerformanceFeedback = showsPerformanceFeedback(design);

  if (!showPerformanceFeedback) {
    return (
      <div className="max-w-5xl mx-auto px-8 py-8 space-y-6">
        <div>
          <SectionHeader title="Game 2 Winnings" />
          <p className="mt-1 text-3xl leading-tight text-gray-600">
            (Your Winnings + {counterpart.capitalizedTerm} Winnings)
          </p>
        </div>
        <section className="rounded-md border border-gray-300 bg-white p-6 space-y-3">
          <div className="text-6xl font-semibold text-gray-900 tabular-nums">
            {dollars(teamWinningsBeforeAdjustment)}
          </div>

          <p className="text-2xl text-black">
            These winnings come from <b>your winnings + your {counterpart.term}'s winnings</b> in Game 2.
          </p>

          <p className="text-2xl text-black">
            You and your {counterpart.term} <b><u>do not</u> know</b> your individual contributions to this total.
          </p>


          <p className="text-2xl text-black">
            You and your {counterpart.term} can <b><u>negotiate</u></b> how to split this money in the
            next section.
          </p>
        </section>

        <div className="flex items-center space-x-4">
          <Button
            handleClick={handleContinue}
            autoFocus
            disabled={player.stage.get("submit")}
            className="text-xl"
          >
            Continue
          </Button>
          {player.stage.get("submit") && !allSubmitted ? (
            <span className="text-lg text-gray-500">
              Waiting for {counterpart.term}...
            </span>
          ) : null}
        </div>
      </div>
    );
  }

  const individualSecondsByPlayerId =
    stage2.individualInCircleSecondsByPlayerId || {};
  const yourInCircleSeconds = Number(
    individualSecondsByPlayerId[player.id] ?? 0
  );
  const partnerInCircleSeconds = Number(
    partner ? individualSecondsByPlayerId[partner.id] ?? 0 : 0
  );
  const yourContributionPercent =
    gameDurationSeconds > 0 ? (yourInCircleSeconds / gameDurationSeconds) * 100 : 0;
  const partnerContributionPercent =
    gameDurationSeconds > 0 ? (partnerInCircleSeconds / gameDurationSeconds) * 100 : 0;
  const yourContributionDollars =
    teamInCircleSeconds > 0
      ? teamWinningsBeforeAdjustment * (yourInCircleSeconds / teamInCircleSeconds)
      : 0;
  const partnerContributionDollars =
    teamInCircleSeconds > 0
      ? teamWinningsBeforeAdjustment * (partnerInCircleSeconds / teamInCircleSeconds)
      : 0;
  const trackingDollarsPerSecond = Math.max(
    0,
    Number(stage2.trackingDollarsPerSecond ?? design.trackingDollarsPerSecond ?? 0)
  );
  const configuredStage2StakeDollars = Number(
    stage2.stage2MaxPerParticipant ?? design.stage2MaxPerParticipant
  );
  const stage2StakeDollars = Math.max(
    0,
    Number.isFinite(configuredStage2StakeDollars)
      ? configuredStage2StakeDollars
      : gameDurationSeconds * trackingDollarsPerSecond
  );
  const teamPlayerCount = Math.max(
    1,
    players.length || Number(design.playerCount) || 2
  );
  const stage2TeamStakeDollars = stage2StakeDollars * teamPlayerCount;
  const teamSavedDollars = clampMoney(
    teamWinningsBeforeAdjustment,
    stage2TeamStakeDollars
  );
  const yourSavedDollars = clampMoney(
    yourContributionDollars,
    stage2StakeDollars
  );
  const partnerSavedDollars = clampMoney(
    partnerContributionDollars,
    stage2StakeDollars
  );
  const yourLostDollars = clampMoney(
    stage2StakeDollars - yourSavedDollars,
    stage2StakeDollars
  );
  const partnerLostDollars = clampMoney(
    stage2StakeDollars - partnerSavedDollars,
    stage2StakeDollars
  );
  const lossToAllocateDollars = clampMoney(
    stage2TeamStakeDollars - teamSavedDollars,
    stage2TeamStakeDollars
  );
  const stage1ScoresByPlayerId = Object.fromEntries(
    (stage1.scores || []).map((score) => [
      score.playerId,
      Number(score.inCircleSeconds ?? 0),
    ])
  );
  const stage1DurationSeconds = Number(stage1.durationSeconds ?? 0);
  const stage1TrackingDollarsPerSecond = Math.max(
    0,
    Number(stage1.trackingDollarsPerSecond ?? design.trackingDollarsPerSecond ?? 0)
  );
  const stage1StakeDollars = Math.max(
    0,
    Number(
      stage1.stage1MaxPerParticipant ??
        design.stage1MaxPerParticipant ??
        stage1DurationSeconds * stage1TrackingDollarsPerSecond
    ) || 0
  );
  const stage1YourSavedDollars = clampMoney(
    (stage1ScoresByPlayerId[player.id] ?? 0) * stage1TrackingDollarsPerSecond,
    stage1StakeDollars
  );
  const stage1PartnerSavedDollars = clampMoney(
    (partner ? stage1ScoresByPlayerId[partner.id] ?? 0 : 0) *
      stage1TrackingDollarsPerSecond,
    stage1StakeDollars
  );
  const stage1YourLostDollars = clampMoney(
    stage1StakeDollars - stage1YourSavedDollars,
    stage1StakeDollars
  );
  const stage1SavedFromOpponentDollars =
    stage1.winnerId === player.id ? stage1PartnerSavedDollars : 0;
  const stage1SavedFromOpponentOffsetDollars = Math.min(
    stage1SavedFromOpponentDollars,
    stage1YourLostDollars
  );
  const stage1SavedFromOpponentSurplusDollars = Math.max(
    0,
    stage1SavedFromOpponentDollars - stage1YourLostDollars
  );
  const stage1LostAfterOpponentSavedDollars = Math.max(
    0,
    stage1YourLostDollars - stage1SavedFromOpponentOffsetDollars
  );
  const stage1SavedFromOpponentSegment =
    stage1SavedFromOpponentDollars > 0
      ? [{
        label: `Game 1 Saved From ${counterpart.capitalizedTerm}`,
        labelOrigin: "Game 1",
        labelEmphasis: `Saved From ${counterpart.capitalizedTerm}`,
        value: stage1SavedFromOpponentDollars,
        background: CONTEXT_SAVED_FROM_OPPONENT_BACKGROUND,
        legendValueClassName: "text-emerald-700",
        ...GAME1_SEGMENT_OUTLINE_PROPS,
      }]
      : [];
  const stage1TakenFromYouDollars =
    stage1.loserId === player.id ? stage1YourSavedDollars : 0;
  const stage1TakenFromYouSegment =
    stage1TakenFromYouDollars > 0
      ? [{
        label: "Game 1 Lost (Taken From You)",
        labelOrigin: "Game 1",
        labelEmphasis: "Lost (Taken From You)",
        value: stage1TakenFromYouDollars,
        background: CONTEXT_TAKEN_BACKGROUND,
        negativeLegendValue: true,
        ...GAME1_SEGMENT_OUTLINE_PROPS,
      }]
      : [];
  const stage1SavedKeptDollars =
    stage1.winnerId === player.id ? stage1YourSavedDollars : 0;
  const stage1SavedKeptSegment =
    stage1SavedKeptDollars > 0
      ? [{
        label: "Game 1 Saved",
        labelOrigin: "Game 1",
        labelEmphasis: "Saved",
        value: stage1SavedKeptDollars,
        color: CONTEXT_SAVED_COLOR,
        legendValueClassName: "text-emerald-700",
        ...GAME1_SEGMENT_OUTLINE_PROPS,
      }]
      : [];
  const game2LossBasisDollars = stage2StakeDollars;
  const game2LostAfterSavingsDollars = Math.max(
    0,
    game2LossBasisDollars - teamSavedDollars
  );
  const game2SavingsEatingLossDollars = Math.min(
    teamSavedDollars,
    game2LossBasisDollars
  );
  const game2SurplusAtStakeDollars = Math.max(
    0,
    teamSavedDollars - game2LossBasisDollars
  );
  const game2SavingsAtStakeDollars =
    game2SavingsEatingLossDollars + game2SurplusAtStakeDollars;
  const game2LostSegment =
    game2LostAfterSavingsDollars > 0
      ? [{
        label: "Game 2 Lost",
        labelOrigin: "Game 2",
        labelEmphasis: "Lost",
        value: game2LostAfterSavingsDollars,
        color: "#dc2626",
        negativeLegendValue: true,
      }]
      : [];
  const game2SavingsAtStakeSegment =
    game2SavingsAtStakeDollars > 0
      ? [{
        label: "Game 2 Savings At Stake",
        labelOrigin: "Game 2",
        labelEmphasis: "Savings At Stake",
        value: game2SavingsAtStakeDollars,
        background: CONTEXT_SAVINGS_AT_STAKE_BACKGROUND,
        legendValueClassName: "text-amber-700",
      }]
      : [];
  const moneyContextTotalDollars =
    stage1StakeDollars +
    stage1SavedFromOpponentSurplusDollars +
    game2LostAfterSavingsDollars +
    game2SavingsAtStakeDollars;
  const playerStartingAllocationDollars =
    stage1StakeDollars + stage2StakeDollars;
  const totalPlayerLostDollars =
    stage1LostAfterOpponentSavedDollars +
    stage1TakenFromYouDollars +
    game2LostAfterSavingsDollars;
  const totalPlayerSurplusDollars =
    stage1SavedFromOpponentSurplusDollars + game2SurplusAtStakeDollars;
  const headlineChangeDollars =
    totalPlayerSurplusDollars - totalPlayerLostDollars;
  const headlineIsPositive = headlineChangeDollars > 0.005;
  const headlineIsNegative = headlineChangeDollars < -0.005;
  const headlineClassName = headlineIsPositive
    ? "text-emerald-700"
    : headlineIsNegative
    ? "text-red-700"
    : "text-gray-900";
  const headlinePrefix = headlineIsPositive
    ? "+"
    : headlineIsNegative
    ? "-"
    : "";
  const headlineAmountDollars =
    headlineIsPositive || headlineIsNegative
      ? Math.abs(headlineChangeDollars)
      : 0;
  const payAtStakeRemainingDollars = Math.max(
    0,
    playerStartingAllocationDollars + headlineChangeDollars
  );

  if (lossFrame) {
    return (
      <div className="max-w-5xl mx-auto px-8 py-6 space-y-7">
        <SectionHeader title="Game 2 Outcome" />

        <section className="rounded-md border border-gray-200 bg-gray-50 p-5 space-y-5">
          <SectionHeader title="Performance" />

          <div className="grid lg:grid-cols-2 gap-4">
            <LossSavedCard
              title="You"
              savedDollars={yourSavedDollars}
              lostDollars={yourLostDollars}
              inCircleSeconds={yourInCircleSeconds}
              durationSeconds={gameDurationSeconds}
            />
            <LossSavedCard
              title={counterpart.capitalizedTerm}
              savedDollars={partnerSavedDollars}
              lostDollars={partnerLostDollars}
              inCircleSeconds={partnerInCircleSeconds}
              durationSeconds={gameDurationSeconds}
            />
          </div>
        </section>

        <section className="rounded-md border border-gray-200 bg-white p-5 space-y-5">
          <SectionHeader title="Pay at Stake">
            <span className="text-3xl">
              What happened to the{" "}
              <span className="font-semibold text-gray-900">
                {dollars(playerStartingAllocationDollars)}
              </span>{" "}
              you started with.
            </span>
          </SectionHeader>

          <div className="rounded-md border border-gray-200 p-5 space-y-4">
            <div className="mt-1 flex flex-wrap items-baseline gap-x-6 gap-y-2">
              <div className={`text-5xl font-semibold ${headlineClassName}`}>
                {headlinePrefix}{dollars(headlineAmountDollars)}
              </div>
              <div className="text-3xl text-gray-700">
                You have{" "}
                <strong className="text-gray-900">
                  {dollars(payAtStakeRemainingDollars)}
                </strong>{" "}
                remaining.
              </div>
            </div>

            <div className="text-3xl text-gray-700">
              You and your {counterpart.term} saved{" "}
              <strong className="text-amber-700">
                {dollars(teamSavedDollars)}
              </strong>{" "}
              in Game 2.
            </div>

            <div className="border-t border-gray-200 pt-5 space-y-4">

              <MoneySegmentBar
                total={moneyContextTotalDollars}
                legendTextClassName="text-3xl"
                legendSwatchClassName="h-6 w-6"
                barHeightClassName="h-16"
                showLegendPercent
                segments={[
                  {
                    label: "Game 1 Lost",
                    labelOrigin: "Game 1",
                    labelEmphasis: "Lost",
                    value: stage1LostAfterOpponentSavedDollars,
                    color: CONTEXT_LOST_COLOR,
                    negativeLegendValue: true,
                    ...GAME1_SEGMENT_OUTLINE_PROPS,
                  },
                  ...stage1TakenFromYouSegment,
                  ...stage1SavedFromOpponentSegment,
                  ...stage1SavedKeptSegment,
                  ...game2LostSegment,
                  ...game2SavingsAtStakeSegment,
                ]}
              />
            </div>
          </div>
        </section>

        <div className="flex items-center space-x-4">
          <Button
            handleClick={handleContinue}
            autoFocus
            disabled={player.stage.get("submit")}
            className="text-xl"
          >
            Continue
          </Button>
          {player.stage.get("submit") && !allSubmitted ? (
            <span className="text-lg text-gray-500">
              Waiting for {counterpart.term}...
            </span>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-8 py-6 space-y-6">
      <SectionHeader title="Game 2 Outcome" />

      <div className="rounded-md border border-gray-200 bg-gray-50 p-5 space-y-4">
        <p className="text-2xl text-gray-600">
          During the <strong>{wholeSeconds(gameDurationSeconds)}</strong> game, your team{" "}
          {lossFrame ? "saved" : "earned"} <strong>{dollars(teamWinningsBeforeAdjustment)}</strong>{" "}
          {lossFrame ? "from being lost" : "from"}{" "}
          a combined <strong>{seconds(teamInCircleSeconds)}</strong> with your cursors in the circle.
        </p>

        <div className="grid lg:grid-cols-2 gap-4">
          <ContributionCard
            title="Your contribution"
            titleSuffix={lossFrame ? "saved from loss" : "to team pot"}
            dollarsValue={yourContributionDollars}
            secondsValue={yourInCircleSeconds}
            totalSecondsValue={gameDurationSeconds}
            percentValue={yourContributionPercent}
            colorClassName="text-blue-700"
            barClassName="bg-blue-600"
          />
          <ContributionCard
            title={`${counterpart.capitalizedTerm} contribution`}
            titleSuffix={lossFrame ? "saved from loss" : "to team pot"}
            dollarsValue={partnerContributionDollars}
            secondsValue={partnerInCircleSeconds}
            totalSecondsValue={gameDurationSeconds}
            percentValue={partnerContributionPercent}
            colorClassName="text-red-700"
            barClassName="bg-red-600"
          />
        </div>
      </div>

      <div className="rounded-md border border-amber-200 bg-amber-50 p-5 text-2xl text-amber-900">
        {lossFrame ? (
          <>
            <b>How much saved money is taken from you</b> in Game 2 will be determined by
            you negotiating with your {counterpart.term} in the next section.
          </>
        ) : (
          <>
            <b>Your pay</b> for Game 2 will be determined by the negotiation with your {counterpart.term} in the next section.
          </>
        )}
      </div>

      <DonutChart
        title={lossFrame ? "Game 2 Money Saved From Loss" : "Game 2 Pot Contributions"}
        legendSide
        chartClassName="w-56 h-56"
        centerValueClassName="fill-blue-700 text-sm font-semibold"
        centerTotalClassName="fill-gray-900 text-sm font-semibold"
        centerLabelClassName="fill-gray-500 text-[10px]"
        legendTextClassName="text-3xl"
        showZeroValueLegendItems
        centerValue={yourContributionDollars}
        centerTotal={teamWinningsBeforeAdjustment}
        segments={[
          { label: lossFrame ? "You saved" : "Your contribution", value: yourContributionDollars, color: "#4f46e5" },
          { label: `${counterpart.capitalizedTerm} ${lossFrame ? "saved" : "contribution"}`, value: partnerContributionDollars, color: "#dc2626" },
        ]}
      />

      <div className="flex items-center space-x-4">
        <Button
          handleClick={handleContinue}
          autoFocus
          disabled={player.stage.get("submit")}
          className="text-xl"
        >
          Continue
        </Button>
        {player.stage.get("submit") && !allSubmitted ? (
          <span className="text-lg text-gray-500">
            Waiting for {counterpart.term}...
          </span>
        ) : null}
      </div>
    </div>
  );
}
